/**
 * OBS 上传（对齐 PC 商城：先拿签名 → 直传 OBS → 创建 ResourceFile）
 * 参考：qixin-pc-mall/packages/pc-mall/src/utils/file.ts + api/common.ts
 *
 * 顺序（与设计器一致）：
 * 1. resourceFileFormQuery.uploadFormData 拿 singleUploadJson + downloadUrl
 * 2. FormData POST 到 OBS uploadUrl
 * 3. pamirsFileMutation.create 登记文件，拿到 id / url
 */
import axios from 'axios';

interface UploadFormDataResult {
  singleUploadJson?: string;
  downloadUrl?: string;
}

interface ResourceFileResult {
  id?: string;
  name?: string;
  type?: string;
  url?: string;
}

export interface UploadedResourceFile {
  id: string;
  url: string;
  name: string;
}

function escapeGraphqlString(value: string) {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function resolveContentType(file: File) {
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.xlsx')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  if (name.endsWith('.xls')) return 'application/vnd.ms-excel';
  return 'application/octet-stream';
}

async function pamirsBaseGraphql<T>(query: string): Promise<T> {
  const response = await fetch('/pamirs/base', {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'base',
    },
    body: JSON.stringify({
      operationName: null,
      variables: {},
      query,
    }),
  });

  if (!response.ok) {
    throw new Error(`文件服务请求失败: ${response.status}`);
  }

  const result = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '文件 GraphQL 请求失败');
  }
  return result.data as T;
}

/** 获取 OBS 直传签名 */
export async function getFileSignature(
  filename: string,
  options?: { fileSize?: number; contentType?: string },
): Promise<UploadFormDataResult> {
  const parts = [`filename: "${escapeGraphqlString(filename)}"`];
  if (options?.fileSize != null && Number.isFinite(options.fileSize)) {
    parts.push(`fileSize: ${Math.max(0, Math.floor(options.fileSize))}`);
  }
  if (options?.contentType) {
    parts.push(`contentType: "${escapeGraphqlString(options.contentType)}"`);
  } else if (filename.toLowerCase().endsWith('.pdf')) {
    parts.push('contentType: "application/pdf"');
  }
  parts.push('accept: ""', 'chunkFiles: []');

  const query = `{
  resourceFileFormQuery {
    uploadFormData(
      resourceFileForm: {${parts.join(', ')}}
    ) {
      downloadUrl
      filename
      fileSize
      accept
      contentType
      cdnKey
      singleUploadJson
      multipartUploadJson
    }
  }
}`;

  const data = await pamirsBaseGraphql<{
    resourceFileFormQuery?: { uploadFormData?: UploadFormDataResult };
  }>(query);

  return data?.resourceFileFormQuery?.uploadFormData ?? {};
}

/** 登记已上传文件 */
export async function createResourceFile(url: string, size: number, name: string) {
  const query = `mutation {
  pamirsFileMutation {
    create(
      data: {
        url: "${escapeGraphqlString(url)}"
        size: "${size}"
        name: "${escapeGraphqlString(name)}"
        type: FILE
      }
    ) {
      id
      name
      type
      url
    }
  }
}`;

  const data = await pamirsBaseGraphql<{
    pamirsFileMutation?: { create?: ResourceFileResult };
  }>(query);

  return data?.pamirsFileMutation?.create ?? {};
}

/**
 * 上传单个本地文件到 OBS，并登记 ResourceFile，返回 id + url（导入任务需要）
 */
export async function uploadFileToObsResource(file: File): Promise<UploadedResourceFile> {
  const contentType = resolveContentType(file);
  const { singleUploadJson, downloadUrl } = await getFileSignature(file.name, {
    fileSize: file.size,
    contentType,
  });
  if (!singleUploadJson || !downloadUrl) {
    throw new Error('获取上传签名失败');
  }

  const parsedJson = JSON.parse(singleUploadJson) as {
    uploadUrl: string;
    uploadFormData: Record<string, string>;
  };

  const formData = new FormData();
  Object.entries(parsedJson.uploadFormData || {}).forEach(([key, value]) => {
    formData.append(key, String(value));
  });
  formData.append('file', file);

  await axios({
    method: 'post',
    url: parsedJson.uploadUrl,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    data: formData,
  });

  const fileObj = await createResourceFile(downloadUrl, file.size, file.name);
  const id = String(fileObj.id ?? '').trim();
  const url = String(fileObj.url ?? '').trim();
  if (!id || !url) {
    throw new Error('上传失败：未返回文件 id/url');
  }
  return {
    id,
    url,
    name: fileObj.name || file.name,
  };
}

/**
 * 上传单个本地文件到 OBS，返回可访问 url（反馈附件等场景）
 */
export async function uploadFileToObs(file: File): Promise<string> {
  const fileObj = await uploadFileToObsResource(file);
  return fileObj.url;
}
